import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';

import { useField } from 'react-final-form';
import { Box, TextField } from '@mui/material';

export const AutocompleteAdapter = ({
  input: { name },
  options,
  optionLabel,
  optionValue,
  initValue,
  setValue,
  submitObject,
  freeSolo,
  disabled,
  getMethodService,
  autoSelect, // BOOLEAN: changed in useEffect to set _value=null, autoComplete if option==1
  ...rest
}: any) => {
  const { input, meta } = useField(name);
  const optLabel = optionLabel ?? 'code';
  const optValue = optionValue ?? 'id';
  const safeOptions = Array.isArray(options) ? options : [];

  const onChange = (_e: SyntheticEvent, value: unknown) => {
    _setValue(value);
    if (value) {
      if (submitObject === true) input.onChange(value);
      else
        input.onChange(
          typeof value === 'string' ? value : (value as Record<string, string>)[optValue] ?? ''
        );
    } else {
      input.onChange('');
    }
    if (setValue) {
      setValue(value);
    }
  };

  const handleDisabled = () => {
    if (freeSolo === true) return false;
    return safeOptions.length === 0;
  };

  const [_value, _setValue] = useState(null);
  const lastAppliedInitKey = useRef('');

  /** Stable string so effects react when init content changes, not only object identity. */
  const initContentKey = useMemo(() => {
    if (initValue == null || initValue === '') {
      return '';
    }
    if (typeof initValue === 'object') {
      return `${String(initValue[optValue] ?? '')}::${String(initValue[optLabel] ?? '')}`;
    }
    return String(initValue);
  }, [initValue, optValue, optLabel]);

  useEffect(() => {
    const safeOptions = options ?? [];
    if (!initValue) {
      if (safeOptions.length === 1 && autoSelect !== false) {
        // Auto-select the single available option if autoSelect is not explicitly false
        onChange(null, safeOptions[0]);
      } else if (
        safeOptions.length > 1 &&
        safeOptions.length < 20 &&
        safeOptions.length > 1 &&
        safeOptions.length < 20 &&
        _value !== null &&
        autoSelect !== false
      ) {
        // Use find method to search for a matching option based on the code property
        const matchedOption = safeOptions.find((option) => option.code === _value.code);

        if (matchedOption) {
          onChange(null, matchedOption);
        } else {
          onChange(null, null);
        }
      }
    } else {
      if (!safeOptions.length) {
        return;
      }
      if (input.value === '' && lastAppliedInitKey.current === initContentKey) {
        return;
      }
      const resolvedInit =
        typeof initValue === 'object' && initValue !== null
          ? safeOptions.find((o) => o[optValue] === initValue[optValue]) ?? initValue
          : safeOptions.find(
              (o) => o[optValue] === initValue || String(o[optValue]) === String(initValue)
            ) ?? initValue;
      onChange(null, resolvedInit);
      lastAppliedInitKey.current = initContentKey;
    }
  }, [options, initContentKey]);

  return (
    <Autocomplete
      disableListWrap
      disableClearable={false}
      freeSolo={freeSolo === true}
      options={options}
      disabled={disabled ? true : handleDisabled()}
      getOptionLabel={(option) =>
        option == null ? '' : typeof option === 'string' ? option : option[optLabel] ?? ''
      }
      defaultValue={initValue ?? null}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          {option == null ? '' : typeof option === 'string' ? option : option[optLabel]}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          {...rest}
          helperText={meta.touched ? meta.error : ''}
          error={meta.error && meta.touched}
          fullWidth
        />
      )}
      isOptionEqualToValue={(option, value) => {
        if (value == null || option == null) return false;
        if (typeof value === 'string') {
          return option[optValue] === value || option[optLabel] === value;
        }
        return option[optValue] === value[optValue];
      }}
      onChange={onChange}
      value={_value}
    />
  );
};
